import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { positionAnalysisService } from '../services/positionAnalysisService';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { useTierPortfolio } from '../hooks/useTierPortfolio';
import TierChip from '../components/portfolio/TierChip';

interface PositionAnalysisItem {
  _id: string;
  symbol: string;
  positionType: string;
  tradeLength: string;
  investmentObjective: string;
  currentPrice: number;
  aiAnalysis: string;
  analysisTimestamp: string;
  status: string;
  errorMessage?: string;
  processingTimeMs?: number;
  createdAt: string;
  updatedAt: string;
}

interface AnalysisSummary {
  totalAnalyses: number;
  successfulAnalyses: number;
  failedAnalyses: number;
  lastAnalysisDate?: string;
  averageProcessingTimeMs: number;
}

interface BulkAnalysisRequest {
  positionType: string;
  tradeLength: string;
  investmentObjective: string;
  riskTier: number;
  maxTickers?: number;
}

const PositionAnalysis: React.FC = () => {
  const { accounts, tierFilter } = useTierPortfolio();
  const [analyses, setAnalyses] = useState<PositionAnalysisItem[]>([]);
  const [summary, setSummary] = useState<AnalysisSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [limit, setLimit] = useState(50);

  // Form state for new analysis
  const [analysisForm, setAnalysisForm] = useState<BulkAnalysisRequest>({
    positionType: 'OPTION',
    tradeLength: '1_week',
    investmentObjective: 'moonshot',
    riskTier: 3,
    maxTickers: 10,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [analysesData, summaryData] = await Promise.all([
        positionAnalysisService.getRecentAnalyses(limit, selectedSymbol),
        positionAnalysisService.getAnalysisSummary(),
      ]);
      setAnalyses(analysesData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading position analyses:', error);
    } finally {
      setLoading(false);
    }
  }, [limit, selectedSymbol]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runBulkAnalysis = async () => {
    try {
      setRunningAnalysis(true);
      const response =
        await positionAnalysisService.runBulkAnalysis(analysisForm);

      if (response.success) {
        alert(
          `Bulk analysis completed!\n✅ Analyzed: ${response.analyzedTickers}\n❌ Failed: ${response.failedTickers}\n⏱️ Time: ${response.processingTimeMs}ms`,
        );
        loadData(); // Refresh the data
      } else {
        alert('Bulk analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Error running bulk analysis:', error);
      alert(
        'Error running bulk analysis. Please check the console for details.',
      );
    } finally {
      setRunningAnalysis(false);
    }
  };

  const cleanupOldAnalyses = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete analyses older than 30 days?',
    );
    if (confirmed) {
      try {
        const result = await positionAnalysisService.cleanupOldAnalyses();
        alert(
          `Cleanup completed! Deleted ${result.deletedCount} old analyses.`,
        );
        loadData(); // Refresh the data
      } catch (error) {
        console.error('Error cleaning up old analyses:', error);
        alert('Error cleaning up old analyses.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'pending':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getPositionTypeColor = (positionType: string) => {
    switch (positionType) {
      case 'OPTION':
        return 'bg-purple-100 text-purple-800';
      case 'EQUITY':
        return 'bg-blue-100 text-blue-800';
      case 'FUTURES':
        return 'bg-orange-100 text-orange-800';
      case 'ETF':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Position analysis"
        description="Risk tier on each analysis should match an enabled account slot (typically Tier 6)."
        thesisTimeframe="1_2_weeks"
      />
      {accounts[0]?.riskTiers?.length ? (
        <div className="flex flex-wrap gap-2">
          {accounts[0].riskTiers.filter((t) => t.enabled).map((t) => (
            <TierChip
              key={t.id}
              riskTier={t.riskTier}
              label={`${t.name} (${Math.round(t.allocationPct * 100)}%)`}
              variant={tierFilter !== 'all' && tierFilter !== t.riskTier ? 'inactive' : 'default'}
            />
          ))}
        </div>
      ) : null}
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Position Analysis Dashboard
        </h1>
        <div className="flex space-x-2">
          <button
            onClick={cleanupOldAnalyses}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Cleanup Old
          </button>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                {summary.totalAnalyses}
              </div>
              <div className="text-sm text-gray-600">Total Analyses</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {summary.successfulAnalyses}
              </div>
              <div className="text-sm text-gray-600">Successful</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">
                {summary.failedAnalyses}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                {summary.averageProcessingTimeMs}ms
              </div>
              <div className="text-sm text-gray-600">Avg Processing Time</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Run New Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Run New Bulk Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position Type
              </label>
              <select
                value={analysisForm.positionType}
                onChange={(e) =>
                  setAnalysisForm({
                    ...analysisForm,
                    positionType: e.target.value,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="EQUITY">EQUITY</option>
                <option value="OPTION">OPTION</option>
                <option value="FUTURES">FUTURES</option>
                <option value="ETF">ETF</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trade Length
              </label>
              <select
                value={analysisForm.tradeLength}
                onChange={(e) =>
                  setAnalysisForm({
                    ...analysisForm,
                    tradeLength: e.target.value,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="intraday">Intraday</option>
                <option value="1_day">1 Day</option>
                <option value="1_week">1 Week</option>
                <option value="1_month">1 Month</option>
                <option value="3_months">3 Months</option>
                <option value="6_months">6 Months</option>
                <option value="1_year">1 Year</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Investment Objective
              </label>
              <select
                value={analysisForm.investmentObjective}
                onChange={(e) =>
                  setAnalysisForm({
                    ...analysisForm,
                    investmentObjective: e.target.value,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="capital_preservation">
                  Capital Preservation
                </option>
                <option value="dividend_income">Dividend Income</option>
                <option value="premium_collection">Premium Collection</option>
                <option value="balanced_growth">Balanced Growth</option>
                <option value="aggressive_growth">Aggressive Growth</option>
                <option value="moonshot">Moonshot</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Tier
              </label>
              <select
                value={analysisForm.riskTier}
                onChange={(e) =>
                  setAnalysisForm({
                    ...analysisForm,
                    riskTier: parseInt(e.target.value),
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={1}>1 - Very Low</option>
                <option value={2}>2 - Low</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4 - High</option>
                <option value={5}>5 - Very High</option>
                <option value={6}>6 - Extreme</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Tickers
              </label>
              <input
                type="number"
                value={analysisForm.maxTickers}
                onChange={(e) =>
                  setAnalysisForm({
                    ...analysisForm,
                    maxTickers: parseInt(e.target.value),
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="1"
                max="1000"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={runBulkAnalysis}
                disabled={runningAnalysis}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
              >
                {runningAnalysis ? 'Running...' : 'Run Analysis'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Symbol Filter
              </label>
              <input
                type="text"
                value={selectedSymbol}
                onChange={(e) =>
                  setSelectedSymbol(e.target.value.toUpperCase())
                }
                placeholder="e.g., AAPL"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limit Results
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSelectedSymbol('');
                  setLimit(50);
                }}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analyses List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Position Analyses ({analyses.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {analyses.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No position analyses found. Run a bulk analysis to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {analyses.map((analysis) => (
                <div
                  key={analysis._id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl font-bold text-gray-900">
                        {analysis.symbol}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getPositionTypeColor(analysis.positionType)}`}
                      >
                        {analysis.positionType}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(analysis.status)}`}
                      >
                        {analysis.status}
                      </span>
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>{formatDate(analysis.analysisTimestamp)}</div>
                      {analysis.processingTimeMs && (
                        <div>{analysis.processingTimeMs}ms</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">
                        Trade Length:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {analysis.tradeLength}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Objective:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {analysis.investmentObjective}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Current Price:
                      </span>
                      <span className="ml-2 text-gray-600">
                        ${analysis.currentPrice.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Analysis Length:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {analysis.aiAnalysis.length} chars
                      </span>
                    </div>
                  </div>

                  {analysis.errorMessage && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="text-sm font-medium text-red-800">
                        Error:
                      </div>
                      <div className="text-sm text-red-700">
                        {analysis.errorMessage}
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      AI Analysis:
                    </div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {analysis.aiAnalysis}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PositionAnalysis;
